import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const formatWhen = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'MMM d, yyyy HH:mm');
};

const AdminTraffic = () => {
  const { token } = useSelector((state) => state.auth);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const API_URL = import.meta.env.VITE_API_URL || 'https://api-invoice.ainixit.com/api';

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API_URL}/admin/traffic`, {
          headers: { Authorization: `Bearer ${token}` },
          params: query ? { q: query } : undefined,
        });
        setData(response.data);
      } catch (error) {
        toast.error('Failed to load traffic');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [API_URL, token, query]);

  const totals = data?.totals || {};
  const visitors = data?.visitors || [];
  const leads = data?.leads || [];

  const csv = useMemo(() => {
    const header = 'email,last_ip,downloads,visits,last_activity';
    const lines = leads.map((lead) =>
      [
        lead.email,
        lead.lastIp,
        lead.downloads || 0,
        lead.hits || 0,
        lead.lastSeen ? new Date(lead.lastSeen).toISOString() : '',
      ].join(',')
    );
    return [header, ...lines].join('\n');
  }, [leads]);

  const downloadCsv = () => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ainix-marketing-emails-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin h-12 w-12 border-t-2 border-b-2 border-primary-600 rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Traffic</h1>
        <p className="text-gray-600 mt-1">
          Admin-only visitor IPs and guest emails. One row per IP + email pair. Same email on another IP appears as another row and in the marketing list.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card">
          <p className="text-sm text-gray-600">Unique visitors</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totals.uniqueVisitors || 0}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Page views</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totals.pageViews || 0}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Guest invoices</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totals.guestInvoices || 0}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Signups</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totals.signups || 0}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Guest → register</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{data?.guestToRegisterPercent || 0}%</p>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Visitors</h2>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setQuery(search.trim());
            }}
          >
            <input
              type="search"
              className="input"
              placeholder="Search IP or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              Search
            </button>
          </form>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3 text-sm text-gray-600">IP</th>
                <th className="text-left py-2 px-3 text-sm text-gray-600">Email</th>
                <th className="text-right py-2 px-3 text-sm text-gray-600">Visits</th>
                <th className="text-right py-2 px-3 text-sm text-gray-600">Downloads</th>
                <th className="text-left py-2 px-3 text-sm text-gray-600">First seen</th>
                <th className="text-left py-2 px-3 text-sm text-gray-600">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {visitors.map((row) => (
                <tr key={`${row.ip}|${row.email}`} className="border-b last:border-0">
                  <td className="py-2 px-3 font-mono text-sm">{row.ip}</td>
                  <td className="py-2 px-3">
                    {row.email || <span className="text-gray-400">—</span>}
                    {row.email && row.downloads > 0 && (
                      <span className="ml-2 text-xs bg-green-50 text-green-800 px-2 py-0.5 rounded-full">
                        same IP + email
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right">{row.hits || 0}</td>
                  <td className="py-2 px-3 text-right">{row.downloads || 0}</td>
                  <td className="py-2 px-3 text-sm text-gray-600">{formatWhen(row.firstSeen)}</td>
                  <td className="py-2 px-3 text-sm text-gray-600">{formatWhen(row.lastSeen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {visitors.length === 0 && (
          <p className="text-sm text-gray-500 py-6 text-center">
            No visitor rows yet. Open the landing page or complete a guest download.
          </p>
        )}
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Emails for marketing</h2>
            <p className="text-sm text-gray-600 mt-1">
              Distinct emails from guest downloads. Last IP is the most recent address for that email.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={downloadCsv}
            disabled={leads.length === 0}
          >
            Download CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3 text-sm text-gray-600">Email</th>
                <th className="text-left py-2 px-3 text-sm text-gray-600">Last IP</th>
                <th className="text-right py-2 px-3 text-sm text-gray-600">Downloads</th>
                <th className="text-right py-2 px-3 text-sm text-gray-600">Visits</th>
                <th className="text-left py-2 px-3 text-sm text-gray-600">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.email} className="border-b last:border-0">
                  <td className="py-2 px-3">{lead.email}</td>
                  <td className="py-2 px-3 font-mono text-sm">{lead.lastIp}</td>
                  <td className="py-2 px-3 text-right">{lead.downloads || 0}</td>
                  <td className="py-2 px-3 text-right">{lead.hits || 0}</td>
                  <td className="py-2 px-3 text-sm text-gray-600">{formatWhen(lead.lastSeen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {leads.length === 0 && (
          <p className="text-sm text-gray-500 py-6 text-center">
            No guest emails yet. A download that collects email will appear here.
          </p>
        )}
      </div>

      <div className="card overflow-x-auto">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Daily</h2>
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-3 text-sm text-gray-600">Date</th>
              <th className="text-right py-2 px-3 text-sm text-gray-600">Visitors</th>
              <th className="text-right py-2 px-3 text-sm text-gray-600">Views</th>
              <th className="text-right py-2 px-3 text-sm text-gray-600">Guest invoices</th>
              <th className="text-right py-2 px-3 text-sm text-gray-600">Signups</th>
              <th className="text-right py-2 px-3 text-sm text-gray-600">Claimed</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((row) => (
              <tr key={row.date} className="border-b last:border-0">
                <td className="py-2 px-3">{row.date}</td>
                <td className="py-2 px-3 text-right">{row.uniqueVisitors}</td>
                <td className="py-2 px-3 text-right">{row.pageViews}</td>
                <td className="py-2 px-3 text-right">{row.guestInvoices}</td>
                <td className="py-2 px-3 text-right">{row.signups}</td>
                <td className="py-2 px-3 text-right">{row.claimedSignups}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!data?.rows || data.rows.length === 0) && (
          <p className="text-sm text-gray-500 py-6 text-center">No daily totals yet.</p>
        )}
      </div>
    </div>
  );
};

export default AdminTraffic;
